export const notesData = [
    {
        title: 'Building a Personal Website from Scratch',
        date: '2024-03-28',
        description: 'Experience sharing on building a modern personal website using React, Tailwind CSS, and Next.js.',
        slug: 'building-personal-website',
        tags: ['React', 'Web Development', 'Tutorial'],
        content: `
            Building a personal website is an exciting journey that allows you to showcase your work and share your thoughts with the world. In this post, I'll share my experience of building this website from scratch using modern web technologies.

            ## Technologies Used

            - React for the frontend framework
            - Tailwind CSS for styling
            - Next.js for server-side rendering
            - MDX for writing content

            ## The Process

            I started by setting up a basic React project using Create React App. Then, I added Tailwind CSS for styling, which has been a game-changer in terms of development speed and consistency.

            ## Key Features

            - Dark mode support
            - Responsive design
            - Fast page loads
            - SEO optimization

            ## Lessons Learned

            Building this website taught me a lot about modern web development practices and the importance of user experience.
        `
    },
    {
        title: 'My 2024 Goals and Plans',
        date: '2024-01-01',
        description: 'Reflecting on 2023 achievements and setting new goals for 2024.',
        slug: '2024-goals-and-plans',
        tags: ['Life', 'Planning'],
        content: `
            As we step into 2024, it's time to reflect on the past year and set new goals for the future. Here's my personal roadmap for the year ahead.

            ## 2023 Achievements

            - Completed my master's degree in Computer Science
            - Published two research papers
            - Learned Portuguese to B2 level

            ## 2024 Goals

            ### Professional Development
            - Contribute to open-source projects
            - Write technical blog posts regularly
            - Master cloud architecture

            ### Personal Growth
            - Read 24 books
            - Improve Portuguese to C1 level
            - Start a tech community

            ## Action Plan

            I believe in setting actionable and measurable goals. Here's how I plan to achieve them...
        `
    },
    {
        title: 'Natural Language Processing Study Notes',
        date: '2023-12-15',
        description: 'Summary of NLP fundamentals, algorithms, and practical experiences.',
        slug: 'nlp-study-notes',
        tags: ['NLP', 'AI', 'Study Notes'],
        content: `
            Natural Language Processing (NLP) is a fascinating field that combines linguistics, computer science, and artificial intelligence. Here are my study notes from exploring this domain.

            ## Fundamental Concepts

            ### Tokenization
            The process of breaking down text into individual words or tokens.

            ### Part of Speech Tagging
            Identifying the grammatical parts of speech in a sentence.

            ### Named Entity Recognition
            Identifying and classifying named entities (people, organizations, locations) in text.

            ## Advanced Topics

            ### Word Embeddings
            - Word2Vec
            - GloVe
            - FastText

            ### Transformers Architecture
            The breakthrough that revolutionized NLP...
        `
    },
    {
        title: 'Portuguese Irregular Verbs Research',
        date: '2023-09-20',
        description: 'Pattern analysis and computational linguistics research on Portuguese irregular verbs.',
        slug: 'portuguese-verbs-research',
        tags: ['Portuguese', 'Linguistics', 'Research'],
        content: `
            Portuguese irregular verbs present unique challenges for both language learners and computational linguistics. This research explores patterns and computational approaches to understanding these irregularities.

            ## Research Objectives

            1. Identify patterns in Portuguese irregular verb conjugations
            2. Develop algorithms for predicting irregular forms
            3. Create a comprehensive database of irregular verbs

            ## Methodology

            Our research employed both traditional linguistic analysis and machine learning approaches...

            ## Key Findings

            ### Pattern Recognition
            We identified several recurring patterns in verb irregularities...

            ### Computational Model
            Our model achieved 92% accuracy in predicting irregular forms...
        `
    },
    {
        title: 'The Future of Web Development',
        date: '2023-08-15',
        description: 'Exploring emerging trends and technologies in web development.',
        slug: 'future-web-development',
        tags: ['Web Development', 'Technology', 'Trends'],
        content: `
            The web development landscape is constantly evolving. Let's explore what the future might hold for developers and users alike.

            ## Current Trends

            ### Web Assembly
            Bringing native performance to the web...

            ### Edge Computing
            Moving computation closer to the user...

            ### AI Integration
            How artificial intelligence is changing web development...

            ## Predictions for the Future

            ### The Rise of No-Code
            Impact on traditional development...

            ### Web3 and Decentralization
            The next evolution of the internet...
        `
    },
    {
        title: 'Machine Learning in Linguistics',
        date: '2023-07-10',
        description: 'How machine learning is revolutionizing linguistic research and language learning.',
        slug: 'ml-linguistics',
        tags: ['Machine Learning', 'Linguistics', 'AI'],
        content: `
            Machine learning is transforming how we study and understand languages. This post explores the intersection of ML and linguistics.

            ## Applications

            ### Language Learning
            - Personalized learning paths
            - Automated feedback systems
            - Pattern recognition in language acquisition

            ### Linguistic Research
            - Corpus analysis
            - Language evolution studies
            - Cross-linguistic pattern discovery

            ## Case Studies

            ### Project 1: Automated Grammar Correction
            Results and methodology...

            ### Project 2: Dialect Classification
            Findings and implications...
        `
    },
    {
        title: 'Building Accessible Web Applications',
        date: '2023-06-01',
        description: 'Best practices and guidelines for creating accessible web applications.',
        slug: 'web-accessibility',
        tags: ['Web Development', 'Accessibility', 'UX'],
        content: `
            Web accessibility is not just a nice-to-have feature—it's a necessity. Here's how to make your web applications accessible to everyone.

            ## Core Principles

            ### Perceivable
            - Alt text for images
            - Captions for videos
            - Clear typography

            ### Operable
            - Keyboard navigation
            - No time limits
            - Clear navigation

            ### Understandable
            - Consistent layout
            - Error identification
            - Clear instructions

            ## Implementation Guide

            Step-by-step process for implementing accessibility features...
        `
    },
    {
        title: 'React Performance Optimization',
        date: '2023-05-15',
        description: 'Advanced techniques for optimizing React application performance.',
        slug: 'react-performance',
        tags: ['React', 'Performance', 'Web Development'],
        content: `
            Performance optimization is crucial for creating smooth, responsive React applications. Let's dive into advanced techniques.

            ## Common Performance Issues

            ### Unnecessary Re-renders
            How to identify and fix...

            ### Bundle Size
            Strategies for code splitting...

            ### Memory Leaks
            Prevention and cleanup...

            ## Optimization Techniques

            ### React.memo
            When and how to use effectively...

            ### useMemo and useCallback
            Proper usage patterns...

            ## Monitoring and Testing

            Tools and methods for performance testing...
        `
    },
    {
        title: 'Introduction to Computational Phonology',
        date: '2023-04-01',
        description: 'Understanding the basics of computational approaches to phonological analysis.',
        slug: 'computational-phonology',
        tags: ['Linguistics', 'Computer Science', 'Phonology'],
        content: `
            Computational phonology bridges the gap between traditional linguistic analysis and computer science. Here's an introduction to this fascinating field.

            ## Basic Concepts

            ### Phonological Rules
            Formal representation and computation...

            ### Sound Patterns
            Digital analysis methods...

            ## Computational Methods

            ### Finite State Automata
            Application in phonological analysis...

            ### Machine Learning Approaches
            Modern computational techniques...

            ## Practical Applications

            Real-world uses in speech technology...
        `
    },
    {
        title: 'The Art of Technical Writing',
        date: '2023-03-10',
        description: 'Guidelines and best practices for effective technical writing.',
        slug: 'technical-writing',
        tags: ['Writing', 'Documentation', 'Communication'],
        content: `
            Good technical writing is essential for sharing knowledge and documenting software. Here's how to excel at it.

            ## Principles of Technical Writing

            ### Clarity
            - Use simple language
            - Avoid jargon
            - Be concise

            ### Structure
            - Logical organization
            - Clear headings
            - Progressive disclosure

            ### Examples
            The importance of practical examples...

            ## Documentation Types

            ### API Documentation
            Best practices and tools...

            ### User Guides
            Writing for different audiences...

            ## Tools and Resources

            Recommended tools for technical writers...
        `
    }
];

export const getNoteBySlug = (slug) => {
    return notesData.find(note => note.slug === slug);
};

export const getNotesByYear = () => {
    const notesByYear = {};
    notesData.forEach(note => {
        const year = new Date(note.date).getFullYear().toString();
        if (!notesByYear[year]) {
            notesByYear[year] = [];
        }
        notesByYear[year].push(note);
    });

    return Object.entries(notesByYear)
        .sort(([yearA], [yearB]) => yearB.localeCompare(yearA))
        .map(([year, entries]) => ({
            year,
            entries: entries.sort((a, b) => new Date(b.date) - new Date(a.date))
        }));
}; 